import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { useContext, useMemo } from "react";
import { readInlineData, useLazyLoadQuery, usePaginationFragment } from "react-relay";
import { useNavigate } from "react-router";
import {
	MODULE_RATING_CALCULATIONS_INLINE_FRAGMENT,
	QUERY_FRAGMENT,
} from "./module-ratings-table.graphql";
import { moduleRatings_Query } from "../../../../../__generated__/moduleRatings_Query.graphql";
import {
	moduleRatingsTable_ModuleRatingCalculationInlineFragment$data,
	moduleRatingsTable_ModuleRatingCalculationInlineFragment$key,
} from "../../../../../__generated__/moduleRatingsTable_ModuleRatingCalculationInlineFragment.graphql";
import { moduleRatingsTable_Refetch } from "../../../../../__generated__/moduleRatingsTable_Refetch.graphql";
import { moduleRatingsTable_RefetchableQueryFragment$key } from "../../../../../__generated__/moduleRatingsTable_RefetchableQueryFragment.graphql";
import { useDebouncesFilters } from "../../../../../infecto-lms-admin/components/core/filters/useDebouncesFilters";
import { getModuleRatingDetailsPath } from "../../../module-rating-details/module-rating-details.paths";
import { ModuleRatingsContext } from "../../module-ratings.context";
import { QUERY } from "../../module-ratings.graphql";

export const ModuleRatingsTable = () => {
	const query = useLazyLoadQuery<moduleRatings_Query>(QUERY,  { first: 10 });
	const { state } = useContext(ModuleRatingsContext);

	const { data, hasPrevious, hasNext, loadPrevious, loadNext, refetch } = usePaginationFragment<
		moduleRatingsTable_Refetch,
		moduleRatingsTable_RefetchableQueryFragment$key
	>(QUERY_FRAGMENT, query);

	const connection = data.Admin.ModuleRating.ModuleRatingCalculations || [];

	const navigate = useNavigate();

	const nodes = useMemo(
		() =>
			connection.edges
				?.filter((e) => !!e?.node)
				.map((e) => e!.node)
				.map((item) =>
					readInlineData<moduleRatingsTable_ModuleRatingCalculationInlineFragment$key>(
						MODULE_RATING_CALCULATIONS_INLINE_FRAGMENT,
						item,
					),
				) ?? [],
		[connection],
	);

	const filters = useMemo(
		(): moduleRatingsTable_Refetch["variables"] => ({
			value:
				(state.to ?? state.from)
					? {
							from: state.from,
							to: state.to,
						}
					: {},
		}),
		[state],
	);
	useDebouncesFilters<moduleRatingsTable_Refetch["variables"]>(filters, refetch);

	return (
		<div>
			<DataTable<moduleRatingsTable_ModuleRatingCalculationInlineFragment$data[]>
				emptyMessage="Keine Modul-Bewertungen"
				className="mb-3"
				value={nodes}
			>
				<Column
					header="Modulname"
					field="node.structureDefinition.title"
					sortable
					body={(item: moduleRatingsTable_ModuleRatingCalculationInlineFragment$data) => (
						<div>{item.node?.structureDefinition.title}</div>
					)}
				/>
				<Column
					header="Score"
					sortable
					field={"score"}
					body={(item: moduleRatingsTable_ModuleRatingCalculationInlineFragment$data) => (
						<div>{item.score}</div>
					)}
				/>
				<Column
					header="Anzahl"
					sortable
					field={"amount"}
					body={(item: moduleRatingsTable_ModuleRatingCalculationInlineFragment$data) => (
						<div>{item.amount}</div>
					)}
				/>

				<Column
					header="Aktionen"
					style={{ justifyItems: "start" }}
					body={(item: moduleRatingsTable_ModuleRatingCalculationInlineFragment$data) => (
						<div>
							<Button
								className="mr-2"
								onClick={() => {
									if (!item) return;
									navigate(getModuleRatingDetailsPath(item.node!.id));
								}}
								icon={"pi pi-eye"}
							/>
						</div>
					)}
				/>
			</DataTable>
			<div className="flex justify-content-center align-items-center">
				<Button
					disabled={!hasPrevious}
					onClick={() => loadPrevious(20)}
					className="mr-3 p-button-secondary"
				>
					Zurück
				</Button>
				<Button
					className="p-button-secondary"
					disabled={!hasNext}
					onClick={() => loadNext(20)}
				>
					Weiter
				</Button>
			</div>
		</div>
	);
};
